<script lang="ts">
  import type { PageData } from './$types';
  import { afterNavigate, goto } from '$app/navigation';
  import { page } from '$app/stores';
  import { jobsStore } from '$lib/stores/Careers/jobsStore';
  import { jobsFiltersStore, type JobFiltersState } from '$lib/stores/Careers/jobFiltersStore';
  import {
    jobsListRequestFromQueryString,
    partialJobsListRequestToQueryString
  } from '$lib/careers/JobsListRequest';
  import CareersLandingThemeProvider from './components/CareersLandingThemeProvider.svelte';
  import AboutBlock from './components/AboutBlock.svelte';
  import Hero from './components/Hero.svelte';
  import JobsFilters from './components/JobsFilters.svelte';
  import NoResults from './components/NoResults.svelte';
  import JobsList from './components/JobsList.svelte';
  import CareersLayout from './components/Layouts/CareersLayout.svelte';
  import { useQuery } from '@sanity/svelte-loader';
  import { GetJobListingPageQuery } from '$lib/sanity/queries/careers';
  import type { JobListingPage as JobListingPageType } from '$lib/sanity/schemas';
  import Schema from '$components/StructuredData/Schema.svelte';
  import { env } from '$env/dynamic/public';
  import BreadcrumbListSchema from '$components/StructuredData/BreadcrumbListSchema.svelte';

  export let data: PageData;

  const query = useQuery<JobListingPageType>(GetJobListingPageQuery, undefined, {
    initial: data.jobListingPage
  });

  $: ({ data: jobListingPage, sourceMap } = $query);

  /**
   * Populate jobsFiltersStore with initial values from the server.
   */
  jobsFiltersStore.set({
    query: data.initialJobsListRequest.query ?? '',
    teams: data.initialJobsListRequest.teams ?? [],
    employeeTypes: data.initialJobsListRequest.employeeTypes ?? [],
    locations: data.initialJobsListRequest.locations ?? [],
    isRemote: data.initialJobsListRequest.isRemote ?? false,
    businessUnits: data.initialJobsListRequest.businessUnits ?? []
  });

  /**
   * Populate jobsStore with initial jobs data from the server and
   * subscribe to the changes of data variable.
   *
   * data variable changes when the new page data is received from the server.
   * This usually happens when the user navigates to a different url and for this
   * page will happen when the user changes the filters.
   */
  $: jobsStore.init(data.jobs.currentPage, {
    total: data.jobs.total,
    jobFunctionCounts: data.jobFunctionCounts
  });

  function updateURLSearchParams(e: CustomEvent<JobFiltersState>) {
    const queryString = partialJobsListRequestToQueryString(e.detail);

    // Only update if the query string is different from the current one.
    if ($page.url.search.replace('?', '') === queryString) {
      return;
    }

    goto(`?${queryString}`, {
      replaceState: false,
      noScroll: true,
      keepFocus: true
    });
  }

  afterNavigate((navigation) => {
    if (navigation.to?.route.id !== '/[[language=language]]/(with-nav)/careers/jobs') {
      return;
    }

    // when navigating to a clean url, reset store
    if (!navigation.to.url.search) {
      jobsFiltersStore.reset();
      return;
    }

    if (navigation.type === 'popstate') {
      const nextFilterState = jobsListRequestFromQueryString(navigation.to.url);
      jobsFiltersStore.set({
        query: nextFilterState.query ?? '',
        teams: nextFilterState.teams ?? [],
        employeeTypes: nextFilterState.employeeTypes ?? [],
        locations: nextFilterState.locations ?? [],
        isRemote: nextFilterState.isRemote ?? false,
        businessUnits: nextFilterState.businessUnits ?? []
      });
    }
  });
</script>

<svelte:head>
  <BreadcrumbListSchema
    items={[
      { name: 'Block', path: '/' },
      { name: 'Careers', path: '/careers' },
      { name: 'Work at Block', path: '/careers/jobs' }
    ]}
  />

  {#if data?.seoAttributes?.structuredData}
    <Schema input={data.seoAttributes.structuredData} pageUrlFallback={env.PUBLIC_BASE_URL} />
  {/if}
</svelte:head>

<CareersLayout>
  <CareersLandingThemeProvider>
    <Hero
      title={jobListingPage.content.header ?? 'Work at Block'}
      {sourceMap}
      path={['content', 'header']}
    />
    <AboutBlock document={jobListingPage} {sourceMap} />
    <JobsFilters values={data.filters} on:filters-change={updateURLSearchParams} />
    <NoResults on:filters-change={updateURLSearchParams} />
    <JobsList />
  </CareersLandingThemeProvider>
</CareersLayout>
